import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Building, DollarSign, TrendingUp, TrendingDown, Home, FileText, ArrowRight, Plus, Filter, BarChart3 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Portfolio = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [assignedDeals, setAssignedDeals] = useState([]);
  const [soldProperties, setSoldProperties] = useState([]);
  const [llcs, setLlcs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('');
  const [filterAssetClass, setFilterAssetClass] = useState('');
  const [filterLlc, setFilterLlc] = useState('');
  const [addHoldingOpen, setAddHoldingOpen] = useState(false);
  const [assignLlcOpen, setAssignLlcOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [newHolding, setNewHolding] = useState({
    address: '', apn: '', asset_class: 'SFR', deal_type: 'Cash',
    acquisition_date: '', purchase_price: '', current_value: '',
    mortgage_balance: '', monthly_rent: '', monthly_expenses: '', llc_id: ''
  });

  useEffect(() => {
    fetchData();
    fetchLlcs();
  }, [sortBy, filterAssetClass, filterLlc]);

  const fetchLlcs = async () => {
    try {
      const res = await axios.get(`${API}/llcs`);
      setLlcs(res.data);
    } catch (error) {
      console.error('Error fetching LLCs:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (sortBy) params.append('sort_by', sortBy);
      if (filterAssetClass) params.append('asset_class', filterAssetClass);

      const [summaryRes, holdingsRes, assignedRes, soldRes] = await Promise.all([
        axios.get(`${API}/portfolio/summary`),
        axios.get(`${API}/portfolio/holdings?${params.toString()}`),
        axios.get(`${API}/portfolio/assigned-deals`),
        axios.get(`${API}/portfolio/sold`)
      ]);
      
      setSummary(summaryRes.data);
      let holdingsData = holdingsRes.data;
      if (filterLlc) {
        holdingsData = holdingsData.filter(h => h.llc_id === filterLlc);
      }
      setHoldings(holdingsData);
      setAssignedDeals(assignedRes.data);
      setSoldProperties(soldRes.data);
    } catch (error) {
      console.error('Error fetching portfolio data:', error);
    } finally {
      setLoading(false);
    }
  };

  const assignLlcToProperty = async (propertyId, llcId) => {
    try {
      await axios.post(`${API}/llcs/${llcId}/assign-property/${propertyId}`);
      fetchData();
      setAssignLlcOpen(false);
      setSelectedProperty(null);
    } catch (error) {
      console.error('Error assigning LLC:', error);
    }
  };

  const createHolding = async () => {
    try {
      const selectedLlc = llcs.find(l => l.id === newHolding.llc_id);
      await axios.post(`${API}/portfolio/holdings`, {
        ...newHolding,
        purchase_price: parseFloat(newHolding.purchase_price) || 0,
        current_value: parseFloat(newHolding.current_value) || parseFloat(newHolding.purchase_price) || 0,
        mortgage_balance: parseFloat(newHolding.mortgage_balance) || 0,
        monthly_rent: parseFloat(newHolding.monthly_rent) || 0,
        monthly_expenses: parseFloat(newHolding.monthly_expenses) || 0,
        llc_name: selectedLlc?.name || null
      });
      setAddHoldingOpen(false);
      setNewHolding({ address: '', apn: '', asset_class: 'SFR', deal_type: 'Cash', acquisition_date: '', purchase_price: '', current_value: '', mortgage_balance: '', monthly_rent: '', monthly_expenses: '', llc_id: '' });
      fetchData();
    } catch (error) {
      console.error('Error creating holding:', error);
    }
  };

  const getAssetClassBadge = (assetClass) => {
    const colors = {
      'Land': 'bg-green-500',
      'SFR': 'bg-blue-500',
      'Section8': 'bg-purple-500',
      'MFH': 'bg-orange-500',
      'JV': 'bg-pink-500'
    };
    return colors[assetClass] || 'bg-gray-500';
  };

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="portfolio-page">
      <div className="mb-8">
        <h1 className="text-4xl font-black tracking-tight mb-2" data-testid="page-title">Portfolio</h1>
        <p className="text-muted-foreground">Comprehensive property portfolio management</p>
      </div>

      {/* Portfolio Summary Header */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-8">
        <Card className="border-border">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Monthly Cash Flow</p>
            <p className={`text-xl font-bold font-mono ${(summary?.total_monthly_cashflow || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              ${(summary?.total_monthly_cashflow || 0).toLocaleString()}
            </p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Monthly Expenses</p>
            <p className="text-xl font-bold font-mono text-red-400">${(summary?.total_monthly_expenses || 0).toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Rent Collected</p>
            <p className="text-xl font-bold font-mono text-green-400">${(summary?.total_monthly_rent || 0).toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Portfolio Value</p>
            <p className="text-xl font-bold font-mono text-primary">${(summary?.total_property_value || 0).toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Total Equity</p>
            <p className="text-xl font-bold font-mono text-accent">${(summary?.total_equity || 0).toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Mortgage Balance</p>
            <p className="text-xl font-bold font-mono">${(summary?.total_mortgage_balance || 0).toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Portfolio Cap Rate</p>
            <p className="text-xl font-bold font-mono text-primary">{summary?.portfolio_cap_rate || 0}%</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Total Return</p>
            <p className="text-xl font-bold font-mono text-green-400">${(summary?.total_return || 0).toLocaleString()}</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="holdings" className="w-full">
        <div className="flex items-center justify-between mb-6">
          <TabsList>
            <TabsTrigger value="holdings" className="flex items-center gap-2">
              <Building size={16} /> Holdings ({holdings.length})
            </TabsTrigger>
            <TabsTrigger value="assigned" className="flex items-center gap-2">
              <FileText size={16} /> Assigned ({assignedDeals.length})
            </TabsTrigger>
            <TabsTrigger value="sold" className="flex items-center gap-2">
              <DollarSign size={16} /> Sold ({soldProperties.length})
            </TabsTrigger>
          </TabsList>

          <div className="flex items-center gap-2">
            <Select value={filterLlc || "all"} onValueChange={(v) => setFilterLlc(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40">
                <Building size={14} className="mr-2" />
                <SelectValue placeholder="Filter by LLC" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All LLCs</SelectItem>
                {llcs.map(llc => (
                  <SelectItem key={llc.id} value={llc.id}>{llc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterAssetClass || "all"} onValueChange={(v) => setFilterAssetClass(v === "all" ? "" : v)}>
              <SelectTrigger className="w-32">
                <Filter size={14} className="mr-2" />
                <SelectValue placeholder="Asset Class" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="Land">Land</SelectItem>
                <SelectItem value="SFR">SFR</SelectItem>
                <SelectItem value="Section8">Section 8</SelectItem>
                <SelectItem value="MFH">Multifamily</SelectItem>
                <SelectItem value="JV">JV</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortBy || "default"} onValueChange={(v) => setSortBy(v === "default" ? "" : v)}>
              <SelectTrigger className="w-32">
                <BarChart3 size={14} className="mr-2" />
                <SelectValue placeholder="Sort By" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Default</SelectItem>
                <SelectItem value="monthly_cashflow">Cash Flow</SelectItem>
                <SelectItem value="equity">Equity</SelectItem>
                <SelectItem value="cap_rate">Cap Rate</SelectItem>
                <SelectItem value="current_value">Value</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Holdings Tab */}
        <TabsContent value="holdings">
          <div className="flex justify-end mb-4">
            <Dialog open={addHoldingOpen} onOpenChange={setAddHoldingOpen}>
              <DialogTrigger asChild>
                <Button className="bg-accent hover:bg-accent/90">
                  <Plus size={16} className="mr-2" /> Add Property
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Add Property to Portfolio</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4 max-h-96 overflow-y-auto">
                  <div>
                    <Label>Address</Label>
                    <Input value={newHolding.address} onChange={(e) => setNewHolding({...newHolding, address: e.target.value})} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Asset Class</Label>
                      <Select value={newHolding.asset_class} onValueChange={(v) => setNewHolding({...newHolding, asset_class: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Land">Land</SelectItem>
                          <SelectItem value="SFR">Single Family</SelectItem>
                          <SelectItem value="Section8">Section 8</SelectItem>
                          <SelectItem value="MFH">Multifamily</SelectItem>
                          <SelectItem value="JV">Joint Venture</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Deal Type</Label>
                      <Select value={newHolding.deal_type} onValueChange={(v) => setNewHolding({...newHolding, deal_type: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Cash">Cash</SelectItem>
                          <SelectItem value="Seller Finance">Seller Finance</SelectItem>
                          <SelectItem value="Subject-To">Subject-To</SelectItem>
                          <SelectItem value="JV">JV</SelectItem>
                          <SelectItem value="BRRRR">BRRRR</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Purchase Price</Label>
                      <Input type="number" value={newHolding.purchase_price} onChange={(e) => setNewHolding({...newHolding, purchase_price: e.target.value})} />
                    </div>
                    <div>
                      <Label>Current Value</Label>
                      <Input type="number" value={newHolding.current_value} onChange={(e) => setNewHolding({...newHolding, current_value: e.target.value})} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Monthly Rent</Label>
                      <Input type="number" value={newHolding.monthly_rent} onChange={(e) => setNewHolding({...newHolding, monthly_rent: e.target.value})} />
                    </div>
                    <div>
                      <Label>Monthly Expenses</Label>
                      <Input type="number" value={newHolding.monthly_expenses} onChange={(e) => setNewHolding({...newHolding, monthly_expenses: e.target.value})} />
                    </div>
                  </div>
                  <div>
                    <Label>Mortgage Balance</Label>
                    <Input type="number" value={newHolding.mortgage_balance} onChange={(e) => setNewHolding({...newHolding, mortgage_balance: e.target.value})} />
                  </div>
                  <div>
                    <Label>Acquisition Date</Label>
                    <Input type="date" value={newHolding.acquisition_date} onChange={(e) => setNewHolding({...newHolding, acquisition_date: e.target.value})} />
                  </div>
                  <div>
                    <Label>Assign to LLC</Label>
                    <Select value={newHolding.llc_id || "none"} onValueChange={(v) => setNewHolding({...newHolding, llc_id: v === "none" ? "" : v})}>
                      <SelectTrigger><SelectValue placeholder="Select LLC (optional)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No LLC Assignment</SelectItem>
                        {llcs.map(llc => (
                          <SelectItem key={llc.id} value={llc.id}>{llc.name} ({llc.tax_classification})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={createHolding} className="w-full">Add to Portfolio</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {holdings.length === 0 ? (
            <Card className="border-border">
              <CardContent className="py-16 text-center">
                <Building size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No properties in portfolio</p>
                <p className="text-sm text-muted-foreground mt-2">Add your first property to get started</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {holdings.map((holding) => (
                <Card 
                  key={holding.id} 
                  className="border-border cursor-pointer hover:border-primary transition-colors"
                  onClick={() => navigate(`/portfolio/${holding.id}`)}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-base">{holding.address}</CardTitle>
                        <p className="text-xs text-muted-foreground">{holding.apn || 'No APN'}</p>
                      </div>
                      <Badge className={getAssetClassBadge(holding.asset_class)}>{holding.asset_class}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Value</p>
                        <p className="font-mono font-bold text-primary">${(holding.current_value || 0).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Equity</p>
                        <p className="font-mono font-bold text-accent">${(holding.equity || 0).toLocaleString()}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Cash Flow</p>
                        <p className={`font-mono font-bold ${(holding.monthly_cashflow || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          ${(holding.monthly_cashflow || 0).toLocaleString()}/mo
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Cap Rate</p>
                        <p className="font-mono font-bold">{holding.cap_rate || 0}%</p>
                      </div>
                    </div>
                    <div className="pt-2 border-t border-border">
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="outline">{holding.deal_type}</Badge>
                        <ArrowRight size={16} className="text-muted-foreground" />
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Building size={12} className="text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">
                            {holding.llc_name || 'No LLC Assigned'}
                          </span>
                        </div>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-6 text-xs px-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedProperty(holding);
                            setAssignLlcOpen(true);
                          }}
                        >
                          {holding.llc_id ? 'Change' : 'Assign'}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Assigned Deals Tab */}
        <TabsContent value="assigned">
          {assignedDeals.length === 0 ? (
            <Card className="border-border">
              <CardContent className="py-16 text-center">
                <FileText size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No assigned deals recorded</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {assignedDeals.map((deal) => (
                <Card key={deal.id} className="border-border">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold">{deal.property_address}</p>
                        <p className="text-sm text-muted-foreground">
                          Seller: {deal.seller_name} → Buyer: {deal.buyer_name}
                        </p>
                        <p className="text-xs text-muted-foreground">Closed: {deal.close_date}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">Assignment Fee</p>
                        <p className="text-2xl font-bold font-mono text-green-400">${(deal.assignment_fee || 0).toLocaleString()}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              <Card className="border-border bg-green-500/10">
                <CardContent className="py-4 text-center">
                  <p className="text-sm text-muted-foreground">Total Assignment Profits</p>
                  <p className="text-3xl font-bold font-mono text-green-400">
                    ${assignedDeals.reduce((sum, d) => sum + (d.assignment_fee || 0), 0).toLocaleString()}
                  </p>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Sold Properties Tab */}
        <TabsContent value="sold">
          {soldProperties.length === 0 ? (
            <Card className="border-border">
              <CardContent className="py-16 text-center">
                <DollarSign size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No sold properties recorded</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {soldProperties.map((prop) => (
                <Card key={prop.id} className="border-border">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold">{prop.property_address}</p>
                        <div className="flex items-center gap-4 mt-1">
                          <span className="text-sm">
                            Purchase: <span className="font-mono">${(prop.purchase_price || 0).toLocaleString()}</span>
                          </span>
                          <span className="text-sm">
                            Sale: <span className="font-mono">${(prop.sale_price || 0).toLocaleString()}</span>
                          </span>
                          <span className="text-sm text-muted-foreground">
                            Held: {prop.holding_months} months
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">Profit</p>
                        <p className={`text-2xl font-bold font-mono ${(prop.profit || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          ${(prop.profit || 0).toLocaleString()}
                        </p>
                        <Badge variant="outline" className="mt-1">{prop.capital_gains_type}</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              <Card className="border-border bg-green-500/10">
                <CardContent className="py-4 text-center">
                  <p className="text-sm text-muted-foreground">Total Sale Profits</p>
                  <p className="text-3xl font-bold font-mono text-green-400">
                    ${soldProperties.reduce((sum, p) => sum + (p.profit || 0), 0).toLocaleString()}
                  </p>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* LLC Assignment Dialog */}
      <Dialog open={assignLlcOpen} onOpenChange={setAssignLlcOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign LLC to Property</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="bg-muted/50 p-3 rounded-lg">
              <p className="text-sm font-medium">{selectedProperty?.address}</p>
              <p className="text-xs text-muted-foreground">
                Current: {selectedProperty?.llc_name || 'No LLC Assigned'}
              </p>
            </div>
            <div className="space-y-2">
              <Label>Select LLC</Label>
              {llcs.map(llc => (
                <div 
                  key={llc.id}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedProperty?.llc_id === llc.id 
                      ? 'border-primary bg-primary/10' 
                      : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => assignLlcToProperty(selectedProperty?.id, llc.id)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{llc.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {llc.tax_classification?.replace('_', ' ')} • {llc.property_count || 0} properties
                      </p>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {llc.purpose || 'General'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
            <Button variant="outline" className="w-full" onClick={() => setAssignLlcOpen(false)}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Portfolio;
